import { reportData } from '../../../../utils/index';

/** hippy的swiper组件的标签名 */
const HIPPY_SWIPER_TAG_NAME = 'hi-swiper';

/**
 * 获取包裹当前组件的滚动容器的DOM元素
 * @param {*} element element 组件的DOM元素
 */
function getScrollEventTarget(element) {
  let target = element;
  while (target) {
    // eslint-disable-next-line no-underscore-dangle
    const isNativeScrollElement = ['ul'].includes(target._tagName);
    const targetStyle = target.style || {};
    const hasXAxisScrollStyle = targetStyle['overflow-x'] === 'scroll';
    const hasYAxisScrollStyle = targetStyle['overflow-y'] === 'scroll';
    if (isNativeScrollElement || hasXAxisScrollStyle || hasYAxisScrollStyle) {
      return target;
    }

    target = target.parentNode;
  }

  return null;
}

/**
 * 获取包裹当前组件的滚动容器的DOM元素列表
 * - 包含额外传入的滚动容器元素列表，支持复杂场景的多层滚动监听
 * @param {*} element element 组件的DOM元素
 * @param {*} extraScrollTargetList 额外传入的滚动容器元素列表
 */
function getScrollEventTargetList(element, extraScrollTargetList) {
  const elementScrollTarget = getScrollEventTarget(element);

  return [elementScrollTarget, ...(extraScrollTargetList || [])];
}

/**
 * 获取当前页面的高度
 * @param {*} Vue
 */
function getClientHeight(Vue) {
  const isNative = !!Vue.Native && Vue.Native.Platform !== 'browser';
  const dimensions = Vue.Native.Dimensions;
  let height = 0;
  if (isNative) {
    height = dimensions.screen.height || dimensions.window.height;
  } else {
    height = document.documentElement.clientHeight || document.body.clientHeight;
  }

  return height;
}

/**
 * 获取当前页面的宽度
 * @param {*} Vue
 */
function getClientWidth(Vue) {
  const isNative = !!Vue.Native && Vue.Native.Platform !== 'browser';
  const dimensions = Vue.Native.Dimensions;
  let width = 0;
  if (isNative) {
    width = dimensions.screen.width || dimensions.window.width;
  } else {
    width = document.documentElement.clientWidth || document.body.clientWidth;
  }

  return width;
}

/**
 * Hippy页面，获取当前组件是否在可视化曝光的区域内
 * - 曝光需要露出组件的1 / 2
 * @param {*} Vue
 * @param {*} element 组件的DOM元素
 */
async function getIsInView(Vue, element) {
  if (!element) {
    return false;
  }
  const {
    width, height, top, left,
  } = await element.getBoundingClientRect();
  // hippy列表元素不在可视区域内时，BCR全部返回-1
  if (width === -1 && height === -1) {
    return false;
  }

  const clientWidth = getClientWidth(Vue);
  const clientHeight = getClientHeight(Vue);
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  // 曝光需要露出组件的1 / 2
  let isInVerticalView = (top >= 0) && (clientHeight - top >= halfHeight);
  isInVerticalView = isInVerticalView || ((top < 0) && (top + height >= halfHeight));
  let isInHorizontalView = (left >= 0) && (clientWidth - left >= halfWidth);
  isInHorizontalView = isInHorizontalView || ((left < 0) && (left + width > width / 2));
  return isInVerticalView && isInHorizontalView;
}

/**
 * 移除当前元素的监听器
 * - 在元素曝光或者移除时触发，降低多个监听器对性能的影响
 * @param {*} el 绑定监听器的元素
 */
function removeListener(el) {
  if (!el?.customScrollList) {
    return;
  }

  for (const item of el.customScrollList) {
    const { scrollTarget, onScroll } = item;
    // 针对hippy-swiper组件的特殊处理
    const scrollEvent = scrollTarget?._tagName === HIPPY_SWIPER_TAG_NAME ? 'pageScroll' : 'scroll';
    scrollTarget.removeEventListener(scrollEvent, onScroll, true);
  }
  el.customScrollList = null;
}

/**
 * 在列表滚动时，监听元素曝光上报处理
 * @param {*} Vue
 * @param {*} el 需要监听曝光的元素
 * @param {*} reportParams 数据上报的参数
 * @param {*} scrollTarget 监听的滚动元素目标
 * @param {*} targetIndex 监听的滚动元素目标索引(用于多滚动目标，监听同个元素的记录)
 */
async function exposureReportOnScroll(Vue, el, reportParams, scrollTarget, targetIndex) {
  if (!scrollTarget) {
    return;
  }

  const onScroll = async () => {
    const isScrollInView = await getIsInView(Vue, el);
    // 判断一下el.customScrollList，防止监听事件已经移除了，函数还在运行
    if (isScrollInView && el.customScrollList) {
      reportData(Vue, reportParams);
      removeListener(el);
    }
  };
  // 这里再包裹一层，因为安卓端addEventListener的callback只能是Function类型，不能是asyncFunction，否则会报错。
  const handleScroll = () => {
    onScroll();
  };
  if (!el.customScrollList) {
    el.customScrollList = [];
  }
  el.customScrollList[targetIndex] = {
    onScroll: handleScroll,
    scrollTarget,
  };
  // 针对hippy-swiper组件的特殊处理
  const scrollEvent = scrollTarget?._tagName === HIPPY_SWIPER_TAG_NAME ? 'pageScroll' : 'scroll';
  scrollTarget.addEventListener(scrollEvent, handleScroll, true);
}

/**
 * 元素曝光上报处理
 * @param {*} Vue
 * @param {*} el 需要监听曝光的元素
 * @param {*} reportParams 数据上报的参数
 */
async function exposureReport(Vue, el, reportParams) {
  const isInView = await getIsInView(Vue, el);
  if (isInView) {
    reportData(Vue, reportParams);
    return;
  }

  // 绑定多个target，使其支持复杂场景的多层滚动监听
  const targetList = getScrollEventTargetList(el, reportParams?.extraScrollTargetList);
  for (let index = 0; index < targetList.length; index++) {
    const target = targetList[index];
    exposureReportOnScroll(Vue, el, reportParams, target, index);
  }
}

/**
 * Hippy页面，曝光数据上报指令
 * @param {*} Vue
 */
function exposureReportInHippy(Vue) {
  return {
    inserted: (el, binding) => {
      if (!binding) {
        return;
      }

      // hippy渲染组件有延迟，因此这里加个定时器
      setTimeout(() => {
        exposureReport(Vue, el, binding?.value);
      }, 1000);
    },
    unbind: (el) => {
      removeListener(el);
    },
  };
}

export { exposureReportInHippy };
